import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import logger from "@/lib/logger";
import { ensureDeferredCatalogLoaded } from "@/main/messages/deferredCatalog";
import type { Localizer } from "@/main/react/Types";

/**
 * The lazy seam in front of `openFeedbackPrompt`, and the only way the eager graph may reach it (FI-719).
 *
 * <p>`FeedbackPromptRuntime` is on the bootstrap path of every page, and its one static import of the
 * opener put `FeedbackPromptDialog` and the `textarea` primitive (7.8 KB) on the startup graph for a
 * dialog that opens 45 seconds after boot, for a member the server has marked due, at most once per
 * load. Anonymous visitors, who can never be due, carried it on every page.
 *
 * <p><b>Preloaded at ARM time, not at fire time.</b> The runtime calls {@link preloadFeedbackPrompt} the
 * moment it arms the dwell timer, which only happens for an eligible member. That keeps the one
 * disruptive failure path of `importChunkWithRetryAndReload` (a stale bundle's reload-once) at boot,
 * where every island chunk takes the same path, instead of 45 seconds into whatever the member is
 * reading. By fire time the chunk is normally already in memory and the dialog opens at once.
 *
 * <p><b>The dialog's copy moved with it.</b> `generate-split-catalogs.mjs` files a key under the DEFERRED
 * catalog half once no eager module references it, so the load also awaits `ensureDeferredCatalogLoaded()`
 * (memoized, never rejects; normally already settled by the page's own island) rather than trusting that
 * some island on this page happened to fetch it. Without it a page with no lazy island would open the
 * dialog in raw keys.
 */
const FEEDBACK_PROMPT_CHUNK_KEY = "feedback-prompt";

type OpenFeedbackPromptModule = typeof import("@/main/react/feedback/openFeedbackPrompt");

let pending: Promise<OpenFeedbackPromptModule> | null = null;

function loadOpenFeedbackPrompt(): Promise<OpenFeedbackPromptModule> {
    if (!pending) {
        pending = Promise.all([
            importChunkWithRetryAndReload(
                () => import("@/main/react/feedback/openFeedbackPrompt").then((m) => ({ default: m })),
                FEEDBACK_PROMPT_CHUNK_KEY,
            ),
            ensureDeferredCatalogLoaded(),
        ])
            .then(([module]) => module.default)
            .catch((error: unknown) => {
                // Forget the failure so the fire-time call gets a fresh attempt instead of the cached rejection.
                pending = null;
                throw error;
            });
    }
    return pending;
}

/** Start fetching the dialog's chunk without opening anything. Never throws, never rejects. */
export function preloadFeedbackPrompt(): void {
    void loadOpenFeedbackPrompt().catch(() => undefined);
}

/**
 * Opens the feedback prompt. Fire-and-forget: the runtime never used the close handle the synchronous
 * opener returned. A chunk that cannot load only logs; the prompt is optional and must never block the page.
 */
export function openFeedbackPrompt(localizer: Localizer): void {
    void loadOpenFeedbackPrompt()
        .then((module) => {
            module.openFeedbackPrompt(localizer);
        })
        .catch((error: unknown) => {
            logger.error("[feedback] failed to load the feedback prompt", error);
        });
}

/** Test-only: drop the cached chunk promise. */
export function resetFeedbackPromptLazyForTests(): void {
    pending = null;
}
